
import React from "react";
import Layout from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  BarChart, 
  LineChart, 
  Users, 
  Wallet, 
  TrendingUp, 
  Package, 
  DollarSign,
  UserCheck, 
  UserMinus,
  ChevronUp,
  ChevronDown,
  Award,
  CreditCard,
  BookOpen
} from "lucide-react";

import { OverviewMetrics } from "@/components/AdminDashboard/OverviewMetrics";
import { BubbleCareStats } from "@/components/AdminDashboard/BubbleCareStats";
import { WalletMetrics } from "@/components/AdminDashboard/WalletMetrics";
import { UserMetrics } from "@/components/AdminDashboard/UserMetrics";
import { ForecastChart } from "@/components/AdminDashboard/ForecastChart";
import { MemberBreakdown } from "@/components/AdminDashboard/MemberBreakdown";
import { MonthlySpending } from "@/components/AdminDashboard/MonthlySpending";
import { PremiumSubscriptions } from "@/components/AdminDashboard/PremiumSubscriptions";

const AdminDashboard = () => {
  // Force render to ensure charts update properly
  const [key, setKey] = React.useState(0);
  
  React.useEffect(() => {
    // Re-render the component once after initial mount
    setKey(prevKey => prevKey + 1);
  }, []);

  return (
    <Layout>
      <div className="container mx-auto py-8 pt-28" key={key}>
        <div className="mb-6 flex flex-col gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground">Monitor and analyze platform metrics</p>
        </div>
        
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart className="h-4 w-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Users
            </TabsTrigger>
            <TabsTrigger value="finance" className="flex items-center gap-2">
              <Wallet className="h-4 w-4" /> Finance
            </TabsTrigger>
            <TabsTrigger value="forecasts" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Forecasts
            </TabsTrigger>
            <TabsTrigger value="premium" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Premium
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-4">
            <OverviewMetrics />
            
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">BubbleCare Distribution</CardTitle>
                  <CardDescription>Total bubbles by category</CardDescription>
                </CardHeader>
                <CardContent>
                  <BubbleCareStats />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Member Breakdown</CardTitle>
                  <CardDescription>Active members by role</CardDescription>
                </CardHeader>
                <CardContent>
                  <MemberBreakdown />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Monthly Spending</CardTitle>
                  <CardDescription>Expenditure trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <MonthlySpending />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="users" className="space-y-4">
            <UserMetrics />
          </TabsContent>
          
          <TabsContent value="finance" className="space-y-4">
            <WalletMetrics />
          </TabsContent>
          
          <TabsContent value="forecasts" className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <ForecastChart />
            </div>
          </TabsContent>

          <TabsContent value="premium" className="space-y-4">
            <PremiumSubscriptions />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
